import {
  ArrowForward,
  Headphones,
  MenuBook,
  EditNote,
  Refresh,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { LearningWeekSummary, LearningActivityChart } from "./LearningActivity";
import { useAppDispatch, useAppSelector, type RootState } from "../../store";
import { selectIsAnonymous } from "../../store/selectors/authSelectors";
import { fetchLearningHome } from "../../store/slices/learningSlice";

export default function LearningHomePage(): React.ReactElement {
  const { t } = useTranslation("home");
  const { t: common } = useTranslation("common");
  const dispatch = useAppDispatch();
  const user = useAppSelector(
    (state): RootState["auth"]["user"] => state.auth.user,
  );
  const anonymous = useAppSelector(selectIsAnonymous);
  const { home, loading, error } = useAppSelector(
    (state): RootState["learning"] => state.learning,
  );
  useEffect((): void => {
    if (user?.id && !anonymous) void dispatch(fetchLearningHome());
  }, [
    dispatch,
    user?.id,
    user?.targetLanguage,
    user?.nativeLanguage,
    user?.level,
    anonymous,
  ]);
  const text = home?.recommendedText;
  return (
    <Box sx={{ py: 1 }}>
      <Typography
        variant="h3"
        sx={{ mb: 1, fontSize: { xs: "1.75rem", md: "2rem" } }}
      >
        {t("title")}
      </Typography>
      <Typography
        color="text.secondary"
        sx={{ mb: 2, maxWidth: 620, display: { xs: "none", sm: "block" } }}
      >
        {t("subtitle")}
      </Typography>
      {home && <LearningWeekSummary home={home} />}
      {error && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            <Button
              onClick={(): void => {
                void dispatch(fetchLearningHome());
              }}
            >
              {t("retry")}
            </Button>
          }
        >
          {t("loadError")}
        </Alert>
      )}
      {loading && !home ? (
        <Skeleton variant="rounded" height={300} />
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper
              data-testid="recommended-practice"
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                bgcolor: "primary.main",
                color: "primary.contrastText",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Headphones />
                <Typography variant="overline">
                  {home?.continuing ? t("continueLabel") : t("recommendLabel")}
                </Typography>
              </Stack>
              <Typography
                variant="h4"
                sx={{
                  mb: 1,
                  fontSize: { xs: "1.5rem", md: "1.75rem" },
                  overflowWrap: "anywhere",
                }}
              >
                {text?.topic || (text ? t("untitled") : t("chooseTitle"))}
              </Typography>
              {text && (
                <Chip
                  size="small"
                  label={`${text.language.toUpperCase()} · ${common(`levels.${text.level}`)}`}
                  sx={{
                    bgcolor: "rgba(255,255,255,.15)",
                    color: "inherit",
                    mb: 1,
                  }}
                />
              )}
              <Typography sx={{ opacity: 0.9, mb: 2, maxWidth: 520 }}>
                {text ? t("practiceHint") : t("chooseDescription")}
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                <Button
                  component={Link}
                  to={text ? `/practice/${text.id}` : "/texts"}
                  variant="contained"
                  endIcon={<ArrowForward />}
                  sx={{
                    bgcolor: "background.paper",
                    color: "primary.main",
                    "&:hover": { bgcolor: "grey.100" },
                  }}
                >
                  {text
                    ? home?.continuing
                      ? t("continue")
                      : t("start")
                    : t("browse")}
                </Button>
                {text && (
                  <Button
                    component={Link}
                    to="/texts"
                    sx={{ color: "inherit" }}
                  >
                    {t("chooseAnother")}
                  </Button>
                )}
              </Stack>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2} data-testid="alternative-practice">
              <Paper elevation={0} sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <MenuBook color="primary" />
                  <Typography variant="h6">{t("wordsTitle")}</Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ my: 1 }}
                >
                  {home && home.reviewWords > 0
                    ? t("wordsReady", { count: home.reviewWords })
                    : t("wordsDescription")}
                </Typography>
                <Button
                  component={Link}
                  to={
                    home && home.reviewWords > 0
                      ? "/dictionary/adaptive"
                      : "/dictionary"
                  }
                  endIcon={<ArrowForward />}
                >
                  {home && home.reviewWords > 0 ? t("review") : t("openWords")}
                </Button>
              </Paper>
              <Paper elevation={0} sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EditNote color="primary" />
                  <Typography variant="h6">{t("dictationTitle")}</Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ my: 1 }}
                >
                  {t("dictationDescription")}
                </Typography>
                <Button
                  component={Link}
                  to="/dictation"
                  endIcon={<ArrowForward />}
                >
                  {t("tryDictation")}
                </Button>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      )}
      {home && <LearningActivityChart home={home} />}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          {t("progressTitle")}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t("progressDescription")}
        </Typography>
        {home &&
        (home.practicedTexts > 0 ||
          home.savedWords > 0 ||
          home.confidentTexts > 0 ||
          home.recentDictation) ? (
          <Paper elevation={0} sx={{ p: { xs: 3, md: 4 } }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t("yourPractice")}
                </Typography>
                <Typography sx={{ mb: 1 }}>
                  {t("practiced", { count: home.practicedTexts })}
                </Typography>
                <Typography color="text.secondary">
                  {t("listening", {
                    count: Math.ceil(home.listeningSeconds / 60),
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("activityNote")}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t("yourResults")}
                </Typography>
                <Typography sx={{ mb: 1 }}>
                  {t("saved", { count: home.savedWords })}
                </Typography>
                <Typography color="text.secondary">
                  {t("confident", { count: home.confidentTexts })}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {home.recentDictation
                    ? t("lastDictation", {
                        accuracy: home.recentDictation.accuracy.toFixed(1),
                      })
                    : t("noChecks")}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        ) : (
          <Typography color="text.secondary">
            {anonymous
              ? t("anonymousProgress")
              : error
                ? t("progressUnavailable")
                : t("firstPractice")}
          </Typography>
        )}
      </Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mt: 3, color: "text.secondary" }}
      >
        <Refresh fontSize="small" />
        <Typography variant="body2">{t("paceNote")}</Typography>
      </Stack>
    </Box>
  );
}
